import React, { useState, useEffect, useCallback } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-datepicker/dist/react-datepicker.css';
import DOMPurify from 'dompurify';
import moment from 'moment';

// Import job form components (these will need to be organized later)
import { JobFormContext } from './job-form/JobFormContext';
import GeneralInfo from './job-form/GeneralInfo';
import Skills from './job-form/Skills';
import Compensation from './job-form/Compensation';
import Description from './job-form/Description';
import { payIsValid } from './job-form/payValidity';
import { processValidationErrors } from '@/domains/profiles/utils/errorHandling';

type SubmitError =
  | { kind: 'validation'; fieldErrors: Record<string, string | null>; messages: string[] }
  | { kind: 'generic'; status?: number; statusText?: string };

// TypeScript interfaces
interface Skill {
  id: number;
  name: string;
  category?: string;
  color?: string;
}

interface JobSkill {
  id: number;
  skill_id: number;
}

interface Job {
  id?: number;
  job_type_id?: number | string;
  practice_id?: number | string;
  position_type?: string;
  description?: string;
  sms_phone?: string;
  temp_date?: string | Date;
  temp_start?: string;
  temp_end?: string;
  compensation_type?: string;
  [key: string]: any;
}

interface Practice {
  id: number;
  name: string;
  [key: string]: any;
}

interface JobType {
  id: number;
  title: string;
  [key: string]: any;
}

interface PositionType {
  id: number;
  title: string;
  [key: string]: any;
}

interface FormElements {
  [key: string]: any;
}

interface EmbedFormProps {
  job: Job;
  practices: Practice[];
  skills: Skill[];
  job_skills: JobSkill[];
  job_types: JobType[];
  position_types: PositionType[];
  form_elements: FormElements;
  skill_categories: string[];
  authenticity_token: string;
  profile_id?: number;
  temp?: boolean;
  onSuccess?: (job?: any) => void;
  onCancel?: () => void;
}

interface JobState extends Job {
  skill_ids: number[];
  position_type: string | null;
  description: string;
  job_type_id: number | string;
}

interface ApiRequestData {
  request_profile: boolean;
  profile_id?: number;
  job: JobState;
}

// Tab styling
const tabStyling: React.CSSProperties = {
  flex: '1',
  textAlign: 'center',
  margin: '5px'
};

/**
 * Create initial job form state
 */
const createJobFormInitialState = (
  job: Job,
  job_skills: JobSkill[],
  temp: boolean
): JobState => {
  // Process temp_date similar to original Form component
  const processedJob = { ...job };

  // Convert temp_date from string to Date object for DatePicker
  if (processedJob.temp_date) {
    try {
      const momentDate = moment(processedJob.temp_date);
      if (momentDate.isValid()) {
        processedJob.temp_date = momentDate.toDate();
      } else {
        console.warn('Invalid temp_date provided:', processedJob.temp_date);
        processedJob.temp_date = null;
      }
    } catch (error) {
      console.error('Error processing temp_date:', error);
      processedJob.temp_date = null;
    }
  }

  // Convert start_date from string to Date object for DatePicker
  if (processedJob.start_date) {
    try {
      const momentDate = moment(processedJob.start_date);
      if (momentDate.isValid()) {
        processedJob.start_date = momentDate.toDate();
      } else {
        console.warn('Invalid start_date provided:', processedJob.start_date);
        processedJob.start_date = null;
      }
    } catch (error) {
      console.error('Error processing start_date:', error);
      processedJob.start_date = null;
    }
  }

  // Set default compensation type if not provided
  if (!processedJob.compensation_type) {
    processedJob.compensation_type = 'hourly';
  }

  return {
    ...processedJob,
    skill_ids: job_skills.map(skill => skill.id),
    position_type: temp ? 'temp' : processedJob.position_type || null,
    description: processedJob.description || '',
    job_type_id: processedJob.job_type_id || 1,
  };
};

/**
 * EmbedForm Component - Enhanced TypeScript version
 * Multi-step job creation/editing form with tabs
 * Handles both regular and temporary job postings
 */
export const EmbedForm: React.FC<EmbedFormProps> = ({
  job,
  practices,
  skills,
  job_skills,
  job_types,
  position_types,
  form_elements,
  skill_categories,
  authenticity_token,
  profile_id,
  temp = false,
  onSuccess,
  onCancel,
}) => {
  // State management
  const [jobState, setJobState] = useState<JobState>(
    () => createJobFormInitialState(job, job_skills, temp)
  );
  const [practicesState, setPracticesState] = useState<Practice[]>(practices);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [validation, setValidation] = useState<boolean>(true);
  const [error, setError] = useState<SubmitError | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [redirecting, setRedirecting] = useState<boolean>(false);

  // Debug logging in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('EmbedForm - practices:', practices);
      console.log('EmbedForm - jobState:', jobState);
    }
  }, [practices, jobState]);

  // Validation logic
  useEffect(() => {
    const {
      position_type,
      job_type_id,
      practice_id,
      sms_phone,
      temp_date,
      temp_start,
      temp_end,
    } = jobState;

    console.log('EmbedForm validation check:', {
      position_type,
      job_type_id,
      practice_id,
      sms_phone,
      temp_date,
      temp_start,
      temp_end
    });

    // Check basic required fields
    const hasBasicFields =
      position_type !== null && position_type !== '' &&
      job_type_id !== null && job_type_id !== '' && job_type_id !== 0 &&
      practice_id !== null && practice_id !== '' && practice_id !== 0;

    if (!hasBasicFields) {
      setValidation(true);
      return;
    }

    // For non-temp positions, basic validation is sufficient
    if (jobState.position_type !== 'temp') {
      setValidation(false);
      return;
    }

    // For temp positions, check additional required fields
    // temp_date can be either string or Date, so check both
    const hasTempDate = temp_date !== null && temp_date !== '' && temp_date !== undefined;
    const hasTempFields =
      sms_phone !== null && sms_phone !== '' &&
      hasTempDate &&
      temp_start !== null && temp_start !== '' &&
      temp_end !== null && temp_end !== '';

    console.log('Temp validation:', {
      hasTempDate,
      hasTempFields,
      sms_phone: !!sms_phone,
      temp_start: !!temp_start,
      temp_end: !!temp_end
    });

    setValidation(!hasTempFields);
  }, [jobState]);

  // Pay is required on every posting. Same story as Form.tsx: the effect above
  // sets `validation` false for any non-temp job with the basics filled in, so
  // a flag published by the Compensation step would be overwritten and the tab
  // strip would let a poster submit with no pay. Derive it from jobState so
  // there is nothing to clobber.
  const payValid = payIsValid(jobState);
  const submitBlocked = validation || !payValid;
  // Skills and Compensation stay reachable while pay is invalid: the
  // Compensation step is where the poster fixes it. Only Description, which
  // carries the submit, waits on pay.

  // API request data
  const apiRequestData: ApiRequestData = {
    request_profile: profile_id !== undefined,
    profile_id: profile_id,
    job: jobState,
  };

  // Submit handler
  const submitToApi = useCallback(async () => {
    // Last line of defence: Description already refuses to call this while
    // submitBlocked, but a posting with no pay must never reach the API.
    if (!payIsValid(jobState)) {
      setTabIndex(2);
      return;
    }

    const url = apiRequestData.job.id
      ? `/jobs/${apiRequestData.job.id}.json`
      : '/jobs.json';
    const method = apiRequestData.job.id ? 'PUT' : 'POST';

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': authenticity_token,
    };

    setLoading(true);
    setError(null);
    let shouldResetLoading = true;

    try {
      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(apiRequestData),
      });

      if (response.ok) {
        // If onSuccess callback is provided, parse response and call it with job data
        if (onSuccess) {
          const data = await response.json();
          // Handle both wrapped ({ job: {...} }) and unwrapped ({...}) responses
          const job = data.job || data;
          setRedirecting(true);
          shouldResetLoading = false;
          onSuccess(job);
          return;
        }

        // Handle successful response (no callback - use redirect)
        if (response.redirected || method === 'PUT') {
          // Remove ".json" from the end of the URL
          const newUrl = response.url.replace(/\.json$/, '');
          const approvedDomains = [
            'directdental.com',
            'test.directdental.com',
            'veracode.directdental.com',
            'localhost'
          ];
          const urlObj = new URL(newUrl);

          if (approvedDomains.includes(urlObj.hostname)) {
            setRedirecting(true);
            shouldResetLoading = false;
            window.location.href = DOMPurify.sanitize(urlObj.href);
          } else {
            console.error('Unapproved redirect URL:', urlObj.hostname);
            setError({ kind: 'generic' });
            if (typeof window.Sentry !== 'undefined') {
              window.Sentry.captureException(
                new Error(`EmbedForm unapproved redirect: ${urlObj.hostname}`),
                { tags: { form: 'EmbedForm', kind: 'unapproved_redirect' } }
              );
            }
          }
        }
      } else {
        console.error('API request failed:', response.status, response.statusText);
        const body = await response.json().catch(() => ({}));

        if (response.status === 422) {
          const fieldErrors = processValidationErrors(body.errors || body);
          const messages = Object.entries(fieldErrors)
            .filter(([, msg]) => !!msg)
            .map(([field, msg]) => {
              const label = field
                .replace(/_/g, ' ')
                .replace(/^\w/, c => c.toUpperCase());
              return `${label} ${msg}`;
            });
          setError({ kind: 'validation', fieldErrors, messages });
        } else {
          setError({
            kind: 'generic',
            status: response.status,
            statusText: response.statusText,
          });
          if (typeof window.Sentry !== 'undefined') {
            window.Sentry.captureException(
              new Error(`EmbedForm submit failed: ${response.status} ${response.statusText}`),
              {
                tags: { form: 'EmbedForm', kind: 'generic', status: String(response.status) },
                extra: {
                  url,
                  method,
                  position_type: apiRequestData.job.position_type,
                  job_id: apiRequestData.job.id,
                },
              }
            );
          }
        }
      }
    } catch (err) {
      console.error('Network error:', err);
      setError({ kind: 'generic' });
      if (typeof window.Sentry !== 'undefined') {
        window.Sentry.captureException(err, {
          tags: { form: 'EmbedForm', kind: 'network' },
          extra: {
            url,
            method,
            position_type: apiRequestData.job.position_type,
            job_id: apiRequestData.job.id,
          },
        });
      }
    } finally {
      if (shouldResetLoading) {
        setLoading(false);
      }
    }
  }, [apiRequestData, authenticity_token, onSuccess]);

  // Tab change handler
  const handleTabSelect = useCallback((index: number) => {
    setTabIndex(index);
  }, []);

  // Render loading state (also show during redirect to prevent form flash)
  if (loading || redirecting) {
    return (
      <div className="d-flex justify-content-center">
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Processing your request...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    const isValidation = error.kind === 'validation';
    const heading = isValidation ? 'Please fix the following' : 'Error';
    const generic = !isValidation ? error : null;
    const statusSuffix = generic?.status ? ` (status: ${generic.status})` : '';

    return (
      <div className="d-flex justify-content-center">
        <div className="alert alert-danger mt-5" role="alert">
          <h4 className="alert-heading">{heading}</h4>
          {isValidation && error.messages.length > 0 ? (
            <ul className="mb-3">
              {error.messages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          ) : (
            <p>
              There was an error processing your request. Please try again
              {statusSuffix}.
            </p>
          )}
          <button
            className="btn btn-outline-danger"
            onClick={() => setError(null)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render main form
  return (
    <div className="d-flex justify-content-center">
      <Tabs
        className="col-12 sign-in-font"
        selectedIndex={tabIndex}
        onSelect={handleTabSelect}
      >
        <TabList className="text-center d-md-flex justify-content-between manageTabList border-0">
          <Tab style={tabStyling}>
            General
          </Tab>
          <Tab
            style={tabStyling}
            disabled={validation}
          >
            Skills
          </Tab>
          <Tab
            style={tabStyling}
            disabled={validation}
          >
            Compensation
          </Tab>
          <Tab
            style={tabStyling}
            disabled={submitBlocked}
          >
            Description
          </Tab>
        </TabList>

        {/* Context Provider */}
        <JobFormContext.Provider value={[jobState, setJobState]}>
          {/* General Information Tab */}
          <TabPanel>
            <GeneralInfo
              practices={practicesState}
              job_types={job_types}
              position_types={position_types}
              tabIndexFunc={setTabIndex}
              validation={validation}
              setValidation={setValidation}
              temp={temp}
              cancelButton={null}
            />
          </TabPanel>

          {/* Skills Tab */}
          <TabPanel>
            <Skills
              skills={skills}
              skill_categories={skill_categories}
              tabIndexFunc={setTabIndex}
            />
          </TabPanel>

          {/* Compensation Tab */}
          <TabPanel>
            <Compensation
              tabIndexFunc={setTabIndex}
              benefitsClass="flex-row justify-content-between"
            />
          </TabPanel>

          {/* Description Tab */}
          <TabPanel>
            <Description
              tabIndexFunc={setTabIndex}
              validation={submitBlocked}
              job_types={job_types}
              form_submit={submitToApi}
            />
          </TabPanel>
        </JobFormContext.Provider>
      </Tabs>
    </div>
  );
};

export default EmbedForm;
